/**
 * Custom hook for handling building action keyboard shortcuts.
 */

import { useEffect, useCallback } from 'react';
import { useBuildingSelectionStore } from '@shared/stores/buildingSelectionStore';
import { useEconomyStore } from '@shared/stores/economyStore';
import { BuildingType, isTowerType } from '@shared/constants/buildings';
import { UnitType } from '@shared/constants/units';
import { canUpgradeBuilding } from '@shared/utils/buildingUpgrade';

interface UseBuildingActionKeyboardShortcutsOptions {
  scene: unknown | null;
}

// Upgrade costs (must match BuildingActionsPanel)
const TOWER_UPGRADE_COST = 25;
const WALL_UPGRADE_COST = 25;
const MINE_UPGRADE_COST = 50;
const SWORDSMAN_COST = 50;
const ARCHER_COST = 75;
const HERO_COST = 200;
const TECH_TOWER_UPGRADE_COST = 100;
const TECH_ARMOR_UPGRADE_COST = 75;
const TECH_DAMAGE_UPGRADE_COST = 75;

/**
 * Hook for handling keyboard shortcuts for building actions.
 *
 * - Tower: 'U' to upgrade
 * - Barracks: 'S' for Swordsman, 'A' for Archer
 * - Hero Spawner: 'H' for Hero
 * - Tech Building: 'T' for Tower upgrade, 'A' for Armor, 'D' for Damage
 * - Wall: 'W' to upgrade
 * - Mine: 'U' to upgrade
 */
export function useBuildingActionKeyboardShortcuts({ scene }: UseBuildingActionKeyboardShortcutsOptions) {
  const selectedBuildingId = useBuildingSelectionStore((state) => state.selectedBuildingId);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Only handle if not typing in an input field
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Only handle if a building is selected
      if (!selectedBuildingId || !scene) return;

      const sceneWithMethods = scene as {
        getBuildingManager?: () => {
          getBuilding: (id: string) => {
            getBuildingType: () => BuildingType;
            getLevel: () => number;
            isConstructed: () => boolean;
            setLevel: (level: number) => void;
            setHealthToMax: () => void;
            upgradeLevel: () => void;
            getPosition: () => { x: number; y: number };
            queueTraining: (unitType: UnitType) => void;
          } | undefined;
        } | null;
        getUnitSpawner?: () => {
          spawnUnit: (unitType: string, x: number, y: number) => void;
        } | null;
        getBuildingUpgradeSystem?: () => {
          getMaxLevel: (buildingType: BuildingType) => number;
          isTowerLevel5Enabled: () => boolean;
          startDirectUpgrade: (buildingId: string, cost: number) => boolean;
          isUpgrading: (buildingId: string) => boolean;
          queueTechResearch: (researchType: 'tower_level_5' | 'armor_upgrade' | 'damage_upgrade', cost: number) => boolean;
        } | null;
      };

      const buildingManager = sceneWithMethods.getBuildingManager?.();
      if (!buildingManager) return;

      const building = buildingManager.getBuilding(selectedBuildingId);
      if (!building || !building.isConstructed()) return;

      const buildingType = building.getBuildingType();
      const level = building.getLevel();
      const key = event.key.toLowerCase();

      const { deductCurrency, hasEnoughCurrency } = useEconomyStore.getState();

      // Tower actions
      if (isTowerType(buildingType)) {
        if (key === 'u') {
          event.preventDefault();
          
          // Check level cap using shared utility
          const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
          if (!canUpgradeBuilding(buildingType, level, upgradeSystem)) {
            return; // Already at max level
          }
          
          // Check if already upgrading
          if (upgradeSystem?.isUpgrading(selectedBuildingId)) {
            return; // Already upgrading
          }
          
          if (hasEnoughCurrency(TOWER_UPGRADE_COST)) {
            if (upgradeSystem) {
              upgradeSystem.startDirectUpgrade(selectedBuildingId, TOWER_UPGRADE_COST);
            }
          }
          return;
        }
      }

      // Barracks actions
      if (buildingType === BuildingType.BARRACKS) {
        if (key === 's') {
          event.preventDefault();
          if (hasEnoughCurrency(SWORDSMAN_COST) && deductCurrency(SWORDSMAN_COST)) {
            const buildingWithTraining = building as unknown as {
              queueTraining: (unitType: UnitType) => void;
            };
            if (buildingWithTraining.queueTraining) {
              buildingWithTraining.queueTraining(UnitType.MELEE);
            }
          }
          return;
        }
        if (key === 'a') {
          event.preventDefault();
          if (hasEnoughCurrency(ARCHER_COST) && deductCurrency(ARCHER_COST)) {
            const buildingWithTraining = building as unknown as {
              queueTraining: (unitType: UnitType) => void;
            };
            if (buildingWithTraining.queueTraining) {
              buildingWithTraining.queueTraining(UnitType.RANGED);
            }
          }
          return;
        }
      }

      // Hero Spawner actions
      if (buildingType === BuildingType.HERO_SPAWNER) {
        if (key === 'h') {
          event.preventDefault();
          if (hasEnoughCurrency(HERO_COST)) {
            // TODO: Implement hero spawning
            console.log('Hero training not yet implemented');
          }
          return;
        }
      }

      // Tech Building actions
      if (buildingType === BuildingType.TECH_BUILDING) {
        if (key === 't') {
          event.preventDefault();
          if (hasEnoughCurrency(TECH_TOWER_UPGRADE_COST)) {
            const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
            if (upgradeSystem) {
              upgradeSystem.queueTechResearch('tower_level_5', TECH_TOWER_UPGRADE_COST);
            }
          }
          return;
        }
        if (key === 'a') {
          event.preventDefault();
          if (hasEnoughCurrency(TECH_ARMOR_UPGRADE_COST)) {
            const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
            if (upgradeSystem) {
              upgradeSystem.queueTechResearch('armor_upgrade', TECH_ARMOR_UPGRADE_COST);
            }
          }
          return;
        }
        if (key === 'd') {
          event.preventDefault();
          if (hasEnoughCurrency(TECH_DAMAGE_UPGRADE_COST)) {
            const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
            if (upgradeSystem) {
              upgradeSystem.queueTechResearch('damage_upgrade', TECH_DAMAGE_UPGRADE_COST);
            }
          }
          return;
        }
      }

      // Wall actions
      if (buildingType === BuildingType.WALL) {
        if (key === 'w') {
          event.preventDefault();
          const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
          // Check if already upgrading
          if (upgradeSystem?.isUpgrading(selectedBuildingId)) {
            return; // Already upgrading
          }
          
          if (hasEnoughCurrency(WALL_UPGRADE_COST)) {
            if (upgradeSystem) {
              upgradeSystem.startDirectUpgrade(selectedBuildingId, WALL_UPGRADE_COST);
            }
          }
          return;
        }
      }

      // Mine actions
      if (buildingType === BuildingType.MINE) {
        if (key === 'u') {
          event.preventDefault();
          const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
          // Check if already upgrading
          if (upgradeSystem?.isUpgrading(selectedBuildingId)) {
            return; // Already upgrading
          }
          
          if (hasEnoughCurrency(MINE_UPGRADE_COST)) {
            if (upgradeSystem) {
              upgradeSystem.startDirectUpgrade(selectedBuildingId, MINE_UPGRADE_COST);
            }
          }
          return;
        }
      }
    },
    [scene, selectedBuildingId]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
}

